<template>
    <v-card :loading="loading">
        <v-card-title class="text-h5">
            <v-btn
                class="mr-3"
                depressed
                color="primary"
                icon
                rounded
                @click="closeDialog"
            >
                <v-icon>mdi-close</v-icon>
            </v-btn>
            {{ currentTitle }}
            <v-spacer />
            <v-btn
                v-if="!createForm"
                depressed
                color="primary"
                rounded
                icon
                @click="update"
            >
                <v-icon>mdi-lead-pencil</v-icon>
            </v-btn>
            <v-btn
                v-if="!createForm"
                depressed
                rounded
                color="primary"
                icon
                @click="openDeleteDialog"
            >
                <v-icon>mdi-delete</v-icon>
            </v-btn>
        </v-card-title>
        <v-dialog
            v-model="deleteDialog"
            :retain-focus="false"
            persistent
            max-width="400px"
        >
            <v-card :loading="loading">
                <v-card-title class="text-h5">Delete Activity</v-card-title>
                <v-card-text>
                    Are you sure you want to delete this activity?
                </v-card-text>
                <v-card-actions>
                    <v-btn text color="secondary" @click="deleteDialog = false">
                        Close
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="deleteActivity"
                        text
                        color="error"
                        :loading="loading"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-card-text class="py-0">
            <v-form
                ref="form"
                v-model="valid"
                :disabled="!createForm && !isEditing"
                class="my-1"
            >
                <v-row class="mt-2">
                    <v-col cols="12" class="py-0">
                        <v-text-field
                            v-model.trim="localActivity.activity"
                            :rules="[rules.required]"
                            label="Activity *"
                            prepend-icon="mdi-clipboard-text-outline"
                            required
                        />
                    </v-col>
                </v-row>
                <v-row class="mt-2">
                    <v-col cols="12" class="py-0">
                        <v-text-field
                            v-model.trim="localActivity.notes"
                            label="Notes"
                            prepend-icon="mdi-text"
                        />
                    </v-col>
                </v-row>
                <v-row class="mt-2">
                    <v-col cols="12" md="6" class="py-0">
                        <v-text-field
                            v-model="localActivity.activityPercentage"
                            :rules="[
                                rules.required,
                                validateActivityPercentage,
                            ]"
                            type="number"
                            min="0"
                            step="any"
                            :max="maxAllowedActivityPercentage"
                            label="Activity Percentage *"
                            prepend-icon="mdi-percent"
                            required
                        />
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                        <v-text-field
                            v-model.number="localActivity.percentageCompleted"
                            readonly
                            disabled
                            type="number"
                            label="Percentage Completed"
                            prepend-icon="mdi-percent"
                        />
                    </v-col>
                </v-row>
                <v-row class="mt-2">
                    <v-col cols="12" class="py-0">
                        <v-select
                            v-model="localActivity.scope"
                            :items="projectScopes"
                            :rules="[rules.required]"
                            label="Scope *"
                            item-text="reference"
                            item-value="id"
                            return-object
                            prepend-icon="mdi-format-list-bulleted-square"
                            required
                        />
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-spacer />
            <v-btn
                v-if="createForm || isEditing"
                text
                color="primary"
                :loading="loading"
                :disabled="
                    !valid || (!createForm && Object.keys(activityDiff) == 0)
                "
                @click="saveActivity"
            >
                Save
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import API from '@/services/api'
import { mapMutations } from 'vuex'
import _ from 'lodash'
import { objDiff } from '@/helpers/objDiff.js'

export default {
    name: 'ActivityLogsForm',
    props: {
        projectId: {
            type: String,
            required: true,
        },
        activity: {
            type: Object,
            default: () => ({}),
        },
        createForm: {
            type: Boolean,
            required: true,
        },
        projectScopes: {
            type: Array,
            default: () => [],
        },
        activities: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            loading: false,
            valid: false,
            isEditing: false,
            deleteDialog: false,
            originalActivity: {},
            localActivity: {
                activity: '',
                notes: '',
                percentageCompleted: 0,
                activityPercentage: null,
                scope: null,
            },
            rules: {
                required: v => !!v || 'Required',
            },
        }
    },
    computed: {
        currentTitle() {
            return this.createForm ? 'New Activity' : 'Edit Activity'
        },
        totalAssignedActivityPercentage() {
            return (this.activities || []).reduce((sum, activity) => {
                return sum + Number(activity.activityPercentage || 0)
            }, 0)
        },
        totalOtherActivitiesPercentage() {
            const currentActivityId =
                !this.createForm && this.localActivity && this.localActivity.id
                    ? this.localActivity.id
                    : null

            return (this.activities || [])
                .filter(activity => {
                    if (!currentActivityId) {
                        return true
                    }
                    return activity.id !== currentActivityId
                })
                .reduce((sum, activity) => {
                    return sum + Number(activity.activityPercentage || 0)
                }, 0)
        },
        remainingActivityPercentage() {
            const remaining = 100 - this.totalOtherActivitiesPercentage
            return remaining > 0 ? remaining : 0
        },
        maxAllowedActivityPercentage() {
            return Math.min(100, this.remainingActivityPercentage)
        },
        activityDiff() {
            if (!this.createForm) {
                return objDiff(this.originalActivity, this.localActivity)
            }
            return null
        },
    },
    mounted() {
        this.localActivity = _.cloneDeep(this.activity)
        if (!this.localActivity || !Object.keys(this.localActivity).length) {
            this.localActivity = {
                activity: '',
                notes: '',
                percentageCompleted: 0,
                activityPercentage: null,
                scope: null,
            }
        }
        if (this.localActivity.percentageCompleted === undefined) {
            this.localActivity.percentageCompleted = 0
        }
        this.originalActivity = _.cloneDeep(this.localActivity)
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        getScopeToSave() {
            const selectedScope = this.localActivity.scope
            if (!selectedScope) {
                return null
            }
            const scopeId = selectedScope.id || selectedScope
            const scopeFromList = (this.projectScopes || []).find(
                scope => scope.id === scopeId
            )
            const resolvedScope = scopeFromList || selectedScope
            return {
                id: resolvedScope.id,
                reference: resolvedScope.reference,
                quoteId: resolvedScope.quoteId,
            }
        },
        update() {
            this.isEditing = !this.isEditing
        },
        openDeleteDialog() {
            this.deleteDialog = true
        },
        validateActivityPercentage(value) {
            const numericValue = Number(value)
            if (
                value === null ||
                value === undefined ||
                value === '' ||
                Number.isNaN(numericValue)
            ) {
                return 'Required'
            }
            if (numericValue <= 0 || numericValue > 100) {
                return 'Value must be greater than 0 and at most 100'
            }
            if (numericValue > this.remainingActivityPercentage) {
                return `The maximum allowed is ${this.remainingActivityPercentage}`
            }
            return true
        },
        closeDialog() {
            this.$refs.form.reset()
            this.isEditing = false
            this.$emit('closeDialog')
        },
        async saveActivity() {
            try {
                const activityPercentageValidation = this.validateActivityPercentage(
                    this.localActivity.activityPercentage
                )
                if (activityPercentageValidation !== true) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: activityPercentageValidation,
                    })
                    return
                }
                this.loading = true
                const payload = {
                    projectId: this.projectId,
                    activity: this.localActivity.activity,
                    notes: this.localActivity.notes,
                    percentageCompleted: this.createForm
                        ? 0
                        : Number(this.localActivity.percentageCompleted || 0),
                    activityPercentage: Number(
                        this.localActivity.activityPercentage
                    ),
                    scope: this.getScopeToSave(),
                }
                if (this.createForm) {
                    const projectActivity = await API.createProjectActivity(
                        payload
                    )
                    this.$emit('addProjectActivity', projectActivity)
                } else {
                    if (Object.keys(this.activityDiff).length === 0) {
                        this.closeDialog()
                        return
                    }
                    const projectActivity = await API.updateProjectActivity({
                        projectId: this.projectId,
                        activityId: this.localActivity.id,
                        ...this.activityDiff,
                    })
                    this.$emit('replaceProjectActivity', projectActivity)
                }
                this.closeDialog()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async deleteActivity() {
            try {
                this.loading = true
                await API.deleteProjectActivity({
                    projectId: this.projectId,
                    activityId: this.localActivity.id,
                })
                this.$emit('removeProjectActivity', this.localActivity)
                this.closeDialog()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
    },
}
</script>
